import React, { useState } from "react";
import {
  Row,
  Column,
  FormGroup,
  TextArea,
  RadioButtonGroup,
  RadioButton,
  TextInput,
  Checkbox,
} from "carbon-components-react";
import useAuth from "../../hooks/useAuth";
import { Controller } from "react-hook-form";
import { useEffect } from "react";

const FinancialPlanForm = ({
  disabled,
  register,
  control,
  errors,
  hasBusinessCurrently,
  setHasBusinessCurrently,
  businessPlan,
  getValues,
  setValue,
  isJury,
}) => {
  const [instalations, setInstalations] = useState(0);
  const [equipment, setEquipment] = useState(0);
  const [workingCapital, setWorkingCapital] = useState(0);
  const [otherInvestments, setOtherInvestments] = useState(0);
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [contingencies, setContingencies] = useState(0);
  const [grossProfit, setGrossProfit] = useState({
    2025: 0,
    2026: 0,
    2027: 0,
  });
  const [indirectCosts, setIndirectCosts] = useState({
    2025: 0,
    2026: 0,
    2027: 0,
  });

  const [totalProvisions, setTotalProvisions] = useState({
    2025: 0,
    2026: 0,
    2027: 0,
  });

  const [product1Sales, setProduct1Sales] = useState({
    2025: 0,
    2026: 0,
    2027: 0,
  });
  const [product2Sales, setProduct2Sales] = useState({
    2025: 0,
    2026: 0,
    2027: 0,
  });

  const [product3Sales, setProduct3Sales] = useState({
    2025: 0,
    2026: 0,
    2027: 0,
  });

  const [otherProductSales, setOtherProductSales] = useState({
    2025: 0,
    2026: 0,
    2027: 0,
  });

  const [totalSales, setTotalSales] = useState({
    2025: 0,
    2026: 0,
    2027: 0,
  });

  const getValue = (businessPlan, entity, field) => {
    const value = getValues(entity + "." + field);
    if (value && !isNaN(parseFloat(value))) {
      return parseFloat(value);
    }
    if (
      businessPlan &&
      businessPlan[entity] &&
      businessPlan[entity][field] &&
      !isNaN(parseFloat(businessPlan[entity][field]))
    ) {
      return parseFloat(businessPlan[entity][field]);
    }
    return 0;
  };

  const calcInvestmentSubTotals = (businessPlan) => {
    const entity = "investmentPlan";
    setInstalations(
      getValue(businessPlan, entity, "installations") +
        getValue(businessPlan, entity, "adaptationOrRehabilitation")
    );
    setEquipment(
      getValue(businessPlan, entity, "machineryAndTools") +
        getValue(businessPlan, entity, "furniture") +
        getValue(businessPlan, entity, "vehicles") +
        getValue(businessPlan, entity, "computersAndDevices") +
        getValue(businessPlan, entity, "motorcycle") +
        getValue(businessPlan, entity, "householdAppliances")
    );
    setWorkingCapital(
      getValue(businessPlan, entity, "rawMaterialOrMerchandise") +
        getValue(businessPlan, entity, "licensesAndFees") +
        getValue(businessPlan, entity, "marketing") +
        getValue(businessPlan, entity, "rent") +
        getValue(businessPlan, entity, "energy") +
        getValue(businessPlan, entity, "water") +
        getValue(businessPlan, entity, "internet") +
        getValue(businessPlan, entity, "officeSupplies")
    );
    setOtherInvestments(contingencies);
  };

  const calcContingencies = () => {
    // Get the value directly from getValues since conectaNegociosFinancing is not nested
    let financingValue = getValues ? getValues("conectaNegociosFinancing") : 0;

    // If no value from form, try to get from businessPlan (for edit/view)
    if (
      (!financingValue || financingValue === 0) &&
      businessPlan &&
      businessPlan.conectaNegociosFinancing
    ) {
      financingValue = businessPlan.conectaNegociosFinancing;
    }

    const numericValue =
      financingValue && !isNaN(parseFloat(financingValue))
        ? parseFloat(financingValue)
        : 0;
    const calculatedContingencies = numericValue * 0.05; // 5% of financing
    setContingencies(calculatedContingencies);
    // Also update the form value
    if (setValue) {
      setValue("investmentPlan.contingencies", calculatedContingencies);
    }
  };

  const calcSales = (businessPlan) => {
    const entity = "salesPlan";
    setProduct1Sales({
      2025:
        getValue(businessPlan, entity, "productService1Price2025") *
        getValue(businessPlan, entity, "productService1Qty2025"),
      2026:
        getValue(businessPlan, entity, "productService1Price2026") *
        getValue(businessPlan, entity, "productService1Qty2026"),
      2027:
        getValue(businessPlan, entity, "productService1Price2027") *
        getValue(businessPlan, entity, "productService1Qty2027"),
    });
    setProduct2Sales({
      2025:
        getValue(businessPlan, entity, "productService2Price2025") *
        getValue(businessPlan, entity, "productService2Qty2025"),
      2026:
        getValue(businessPlan, entity, "productService2Price2026") *
        getValue(businessPlan, entity, "productService2Qty2026"),
      2027:
        getValue(businessPlan, entity, "productService2Price2027") *
        getValue(businessPlan, entity, "productService2Qty2027"),
    });
    setProduct3Sales({
      2025:
        getValue(businessPlan, entity, "productService3Price2025") *
        getValue(businessPlan, entity, "productService3Qty2025"),
      2026:
        getValue(businessPlan, entity, "productService3Price2026") *
        getValue(businessPlan, entity, "productService3Qty2026"),
      2027:
        getValue(businessPlan, entity, "productService3Price2027") *
        getValue(businessPlan, entity, "productService3Qty2027"),
    });
    setOtherProductSales({
      2025:
        getValue(businessPlan, entity, "otherProductServicePrice2025") *
        getValue(businessPlan, entity, "otherProductServiceQty2025"),
      2026:
        getValue(businessPlan, entity, "otherProductServicePrice2026") *
        getValue(businessPlan, entity, "otherProductServiceQty2026"),
      2027:
        getValue(businessPlan, entity, "otherProductServicePrice2027") *
        getValue(businessPlan, entity, "otherProductServiceQty2027"),
    });
  };

  const calcTotalSales = () => {
    setTotalSales({
      2025:
        product1Sales[2025] +
        product2Sales[2025] +
        product3Sales[2025] +
        otherProductSales[2025],
      2026:
        product1Sales[2026] +
        product2Sales[2026] +
        product3Sales[2026] +
        otherProductSales[2026],
      2027:
        product1Sales[2027] +
        product2Sales[2027] +
        product3Sales[2027] +
        otherProductSales[2027],
    });
  };

  const calcGrossProfit = (businessPlan) => {
    const entity = "provisionalPlan";
    setGrossProfit({
      2025:
        getValue(businessPlan, entity, "salesVolume2025") -
        getValue(businessPlan, entity, "costs2025"),
      2026:
        getValue(businessPlan, entity, "salesVolume2026") -
        getValue(businessPlan, entity, "costs2026"),
      2027:
        getValue(businessPlan, entity, "salesVolume2027") -
        getValue(businessPlan, entity, "costs2027"),
    });
  };

  const calcIndirectCosts = (businessPlan) => {
    const entity = "provisionalPlan";
    setIndirectCosts({
      2025: getValue(businessPlan, entity, "otherCostsNotCovered2025"),
      2026: getValue(businessPlan, entity, "otherCostsNotCovered2026"),
      2027: getValue(businessPlan, entity, "otherCostsNotCovered2027"),
    });
  };

  const calcTotalProvisions = () => {
    setTotalProvisions({
      2025: grossProfit[2025] - indirectCosts[2025],
      2026: grossProfit[2026] - indirectCosts[2026],
      2027: grossProfit[2027] - indirectCosts[2027],
    });
  };

  const calcTotalInvestment = () => {
    setTotalInvestment(
      instalations + equipment + workingCapital + otherInvestments
    );
  };

  const handleInvestmentBlur = (e) => {
    if (!isNaN(e.target.value)) {
      calcInvestmentSubTotals(businessPlan);
    }
  };

  const handleSalesBlur = (e) => {
    if (!isNaN(e.target.value)) {
      calcSales(businessPlan);
    }
  };

  const handleProvisionsBlur = (e) => {
    if (!isNaN(e.target.value)) {
      calcGrossProfit(businessPlan);
      calcIndirectCosts(businessPlan);
    }
  };

  const handleFinancingBlur = (e) => {
    if (!isNaN(e.target.value)) {
      calcContingencies();
    }
  };

  useEffect(() => {
    if (businessPlan) {
      if (businessPlan.investmentPlan) {
        calcInvestmentSubTotals(businessPlan);
      }
      if (businessPlan.salesPlan) {
        calcSales(businessPlan);
      }
      if (businessPlan.provisionalPlan) {
        calcGrossProfit(businessPlan);
        calcIndirectCosts(businessPlan);
      }
    }
    calcTotalInvestment();
    calcTotalSales();
    calcTotalProvisions();
    calcContingencies();
  }, [businessPlan]);

  useEffect(() => {
    calcTotalInvestment();
  }, [instalations, equipment, workingCapital, otherInvestments]);

  useEffect(() => {
    calcTotalSales();
  }, [product1Sales, product2Sales, product3Sales, otherProductSales]);

  useEffect(() => {
    calcTotalProvisions();
  }, [grossProfit, indirectCosts]);

  useEffect(() => {
    calcInvestmentSubTotals(businessPlan);
  }, [contingencies]);

  return (
    <FormGroup
      legendText="5.	PLANO FINANCEIRO E FONTES DE FINANCIAMENTO"
      className="section-header"
    >
      {!isJury && (
        <fieldset className="bx--fieldset ">
          <legend className="bx--label required">
            5.1 Plano de Investimento e custos com o capital do programa Conecta
            Negócios – Ligações de Consumo
          </legend>
          <div className="bx--form__helper-text">
            [Descreva os itens que vão constituir o plano de investimento a ser
            coberto somente pelo valor do financiamento a ser adquirido através
            do Conecta Negócios – Ligações de Negócio, indicando o valor em MZN.
            Por favor, considere valores reais de acordo com a sua pesquisa de
            mercado. E note que, ao registar os números coloque-os redondos, ou
            seja, números inteiros. ]
          </div>
          <table className="table table-bordered ">
            <thead className="thead-light">
              <tr>
                <th scope="col">Descrição do Item (preenche os aplicáveis)</th>
                <th scope="col" className="w-25">
                  Montante (MZN)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>Instalações</strong>
                </td>
                <td className="text-right">
                  <strong>{instalations}</strong>
                </td>
              </tr>

              <tr>
                <td>Adaptação ou reabilitação </td>
                <td>
                  <TextInput
                    labelText=""
                    id="adaptationOrRehabilitation"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.adaptationOrRehabilitation")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.adaptationOrRehabilitation?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Equipamento</strong>
                </td>
                <td className="text-right">
                  <strong>{equipment}</strong>
                </td>
              </tr>
              <tr>
                <td>Maquinaria e ferramentas</td>
                <td>
                  <TextInput
                    labelText=""
                    id="machineryAndTools"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.machineryAndTools")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.machineryAndTools?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Mobiliário</td>
                <td>
                  <TextInput
                    labelText=""
                    id="furniture"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.furniture")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.furniture?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Viaturas</td>
                <td>
                  <TextInput
                    labelText=""
                    id="vehicles"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.vehicles")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.vehicles?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Computadores e outros dispositivos </td>
                <td>
                  <TextInput
                    labelText=""
                    id="computersAndDevices"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.computersAndDevices")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.computersAndDevices?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Motorizada</td>
                <td>
                  <TextInput
                    labelText=""
                    id="motorcycle"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.motorcycle")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.motorcycle?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Electrodomésticos</td>
                <td>
                  <TextInput
                    labelText=""
                    id="householdAppliances"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.householdAppliances")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.householdAppliances?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Capital Giro</strong>
                </td>
                <td className="text-right">
                  <strong>{workingCapital}</strong>
                </td>
              </tr>
              <tr>
                <td>Aquisição de matéria prima ou mercadoria</td>
                <td>
                  <TextInput
                    labelText=""
                    id="rawMaterialOrMerchandise"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.rawMaterialOrMerchandise")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.rawMaterialOrMerchandise?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Pagamento de licenças e taxas</td>
                <td>
                  <TextInput
                    labelText=""
                    id="licensesAndFees"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.licensesAndFees")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.licensesAndFees?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Despesas de marketing</td>
                <td>
                  <TextInput
                    labelText=""
                    id="marketing"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.marketing")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.marketing?.message}
                  </p>
                </td>
              </tr>

              <tr>
                <td>Renda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="rent"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.rent")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.rent?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Energia</td>
                <td>
                  <TextInput
                    labelText=""
                    id="energy"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.energy")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.energy?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Água</td>
                <td>
                  <TextInput
                    labelText=""
                    id="water"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.water")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.water?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Internet</td>
                <td>
                  <TextInput
                    labelText=""
                    id="internet"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.internet")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.internet?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Consumíveis de Escritório</td>
                <td>
                  <TextInput
                    labelText=""
                    id="officeSupplies"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.officeSupplies")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.officeSupplies?.message}
                  </p>
                </td>
              </tr>

              <tr>
                <td>Contingências (máximo de 5% do orçamento total)</td>
                <td>
                  <TextInput
                    labelText=""
                    id="contingencies"
                    maxLength={12}
                    disabled={true}
                    value={contingencies}
                    {...register("investmentPlan.contingencies")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.contingencies?.message}
                  </p>
                </td>
              </tr>
              <tr className="table-secondary">
                <td>
                  <strong>
                    Total Geral de Investimento coberto pelo valor da CPN:
                  </strong>
                </td>
                <td className="text-right">
                  <strong>{parseFloat(totalInvestment)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </fieldset>
      )}

      {!isJury && (
        <fieldset className="bx--fieldset mb-3">
          <legend className="bx--label required">5.2 Plano de Vendas </legend>
          <div className="bx--form__helper-text">
            [Indique o volume, preço de venda, valor de venda anual até pelo
            menos 3 anos. Dado que uma empresa pode fornecer mais do que um
            produto (ou serviço), o(a) candidato(a) deve indicar a designação
            específica de cada um deles na tabela abaixo.]
          </div>
          <table className="table table-bordered ">
            <thead className="thead-light">
              <tr>
                <th scope="col" rowSpan={2}>
                  Descrição
                </th>
                <th scope="col" rowSpan={2}>
                  Detalhes
                </th>
                <th scope="col" colSpan={3}>
                  Valor (em MZN)
                </th>
              </tr>
              <tr>
                <th scope="col">2025</th>
                <th scope="col">2026</th>
                <th scope="col">2027</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td rowSpan={3}>
                  <TextArea
                    id="productService1Desc"
                    labelText="Produto/Serviço 1"
                    rows={5}
                    maxLength={255}
                    disabled={disabled}
                    {...register("salesPlan.productService1Desc")}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Desc?.message}
                  </p>
                </td>
                <td>Preço de Venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService1Price2025"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService1Price2025")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Price2025?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService1Price2026"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService1Price2026")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Price2026?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService1Price2027"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService1Price2027")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Price2027?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Quantidades previstas para a venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService1Qty2025"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService1Qty2025")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Qty2025?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService1Qty2026"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService1Qty2026")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Qty2026?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService1Qty2027"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService1Qty2027")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Qty2027?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Volume de vendas (preço*quantidade)</strong>
                </td>
                <td className="text-right">
                  <strong>{product1Sales[2025]}</strong>
                </td>
                <td className="text-right">
                  <strong>{product1Sales[2026]}</strong>
                </td>
                <td className="text-right">
                  <strong>{product1Sales[2027]}</strong>
                </td>
              </tr>
              <tr>
                <td rowSpan={3}>
                  <TextArea
                    id="productService2Desc"
                    labelText="Produto/Serviço 2"
                    rows={5}
                    maxLength={255}
                    disabled={disabled}
                    {...register("salesPlan.productService2Desc")}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Desc?.message}
                  </p>
                </td>
                <td>Preço de Venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService2Price2025"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService2Price2025")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Price2025?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService2Price2026"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService2Price2026")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Price2026?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService2Price2027"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService2Price2027")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Price2027?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Quantidades previstas para a venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService2Qty2025"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService2Qty2025")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Qty2025?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService2Qty2026"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService2Qty2026")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Qty2026?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService2Qty2027"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService2Qty2027")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Qty2027?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Volume de vendas (preço*quantidade)</strong>
                </td>
                <td className="text-right">
                  <strong>{product2Sales[2025]}</strong>
                </td>
                <td className="text-right">
                  <strong>{product2Sales[2026]}</strong>
                </td>
                <td className="text-right">
                  <strong>{product2Sales[2027]}</strong>
                </td>
              </tr>
              <tr>
                <td rowSpan={3}>
                  <TextArea
                    id="productService3Desc"
                    labelText="Produto/Serviço 3"
                    rows={5}
                    maxLength={255}
                    disabled={disabled}
                    {...register("salesPlan.productService3Desc")}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Desc?.message}
                  </p>
                </td>
                <td>Preço de Venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService3Price2025"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService3Price2025")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Price2025?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService3Price2026"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService3Price2026")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Price2026?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService3Price2027"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService3Price2027")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Price2027?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Quantidades previstas para a venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService3Qty2025"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService3Qty2025")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Qty2025?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService3Qty2026"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService3Qty2026")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Qty2026?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService3Qty2027"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService3Qty2027")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Qty2027?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Volume de vendas (preço*quantidade)</strong>
                </td>
                <td className="text-right">
                  <strong>{product3Sales[2025]}</strong>
                </td>
                <td className="text-right">
                  <strong>{product3Sales[2026]}</strong>
                </td>
                <td className="text-right">
                  <strong>{product3Sales[2027]}</strong>
                </td>
              </tr>
              <tr>
                <td rowSpan={3}>
                  <TextArea
                    id="otherProductServiceDesc"
                    labelText="Outro(s) produto(s) / serviço(s)"
                    rows={5}
                    maxLength={255}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServiceDesc")}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServiceDesc?.message}
                  </p>
                </td>
                <td>Preço de Venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="otherProductServicePrice2025"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServicePrice2025")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServicePrice2025?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="otherProductServicePrice2026"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServicePrice2026")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServicePrice2026?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="otherProductServicePrice2027"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServicePrice2027")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServicePrice2027?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Quantidades previstas para a venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="otherProductServiceQty2025"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServiceQty2025")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServiceQty2025?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="otherProductServiceQty2026"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServiceQty2026")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServiceQty2026?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="otherProductServiceQty2027"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServiceQty2027")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServiceQty2027?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Volume de vendas (preço*quantidade)</strong>
                </td>
                <td className="text-right">
                  <strong>{otherProductSales[2025]}</strong>
                </td>
                <td className="text-right">
                  <strong>{otherProductSales[2026]}</strong>
                </td>
                <td className="text-right">
                  <strong>{otherProductSales[2027]}</strong>
                </td>
              </tr>
              <tr className="table-secondary">
                <td colSpan={2}>
                  <strong>Total de Vendas</strong>
                </td>
                <td className="text-right">
                  <strong>{totalSales[2025]}</strong>
                </td>
                <td className="text-right">
                  <strong>{totalSales[2026]}</strong>
                </td>
                <td className="text-right">
                  <strong>{totalSales[2027]}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </fieldset>
      )}
      <fieldset className="bx--fieldset">
        <legend className="bx--label required">
          5.3 Plano de Exploração Previsional{" "}
        </legend>
        <div className="bx--form__helper-text">
          [Preencha a tabela que se segue, indicando as vendas previsionais,
          resultados brutos, custos e resultados líquidos que o seu plano de
          negócio propõe-se atingir]
        </div>
        <table className="table table-bordered ">
          <thead className="thead-light">
            <tr>
              <th scope="col" rowSpan={2}>
                Descrição
              </th>
              <th scope="col" colSpan={3}>
                Valor (MZN)
              </th>
            </tr>
            <tr>
              <th scope="col">2025</th>
              <th scope="col">2026</th>
              <th scope="col">2027</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Volume de vendas (Transportar da Tabela da Secção 5.2)</td>
              <td>
                <TextInput
                  labelText=""
                  id="salesVolume2025"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.salesVolume2025")}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.salesVolume2025?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="salesVolume2026"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.salesVolume2026")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.salesVolume2026?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="salesVolume2027"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.salesVolume2027")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.salesVolume2027?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>Custos (Transportar da Tabela da Secção 5.1)</td>
              <td>
                <TextInput
                  labelText=""
                  id="costs2025"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.costs2025")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.costs2025?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="costs2026"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.costs2026")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.costs2026?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="costs2027"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.costs2027")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.costs2027?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>Outros Custos não Cobertos pelo CONECTA</td>
              <td>
                <TextInput
                  labelText=""
                  id="otherCostsNotCovered2025"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.otherCostsNotCovered2025")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.otherCostsNotCovered2025?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="otherCostsNotCovered2026"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.otherCostsNotCovered2026")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.otherCostsNotCovered2026?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="otherCostsNotCovered2027"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.otherCostsNotCovered2027")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.otherCostsNotCovered2027?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Lucro Bruto</strong>
              </td>
              <td className="text-right">
                <strong>{grossProfit[2025]}</strong>
              </td>
              <td className="text-right">
                <strong>{grossProfit[2026]}</strong>
              </td>
              <td className="text-right">
                <strong>{grossProfit[2027]}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </fieldset>

      <fieldset className="bx--fieldset">
        <legend className="bx--label required">
          5.4 Necessidade de Financiamento
        </legend>
        <div className="bx--form__helper-text">
          [Instrução de preenchimento: Preencha os valores, em meticais,
          pretendidos para a implementação do seu plano de negócio. Não preencha
          em "fundos próprios" e "outras fontes" se não tiver evidências da
          disponibilidade.]
        </div>
        <div className="bx--form__helper-text">
          Para que fins será usado o financiamento?
        </div>
        <Row>
          <Column sm={12} md={4} lg={4}>
            <Controller
              name="coverOperationalCosts"
              control={control}
              render={({ field }) => (
                <Checkbox
                  labelText="1) para cobrir os custos operacionais previstos"
                  id="coverOperationalCosts"
                  disabled={disabled}
                  checked={field.value || false}
                  onChange={(checked) => field.onChange(checked)}
                />
              )}
            />
          </Column>
          <Column sm={12} md={4} lg={4}>
            <Controller
              name="financeBusinessExpansion"
              control={control}
              render={({ field }) => (
                <Checkbox
                  labelText="2) Para financiar a expansão do negócio"
                  id="financeBusinessExpansion"
                  disabled={disabled}
                  checked={field.value || false}
                  onChange={(checked) => field.onChange(checked)}
                />
              )}
            />
          </Column>
          <Column sm={12} md={4} lg={4}>
            <Controller
              name="addNewProductOrService"
              control={control}
              render={({ field }) => (
                <Checkbox
                  labelText="3) Para adicionar outro produto ou serviço à oferta actual "
                  id="addNewProductOrService"
                  disabled={disabled}
                  checked={field.value || false}
                  onChange={(checked) => field.onChange(checked)}
                />
              )}
            />
          </Column>
        </Row>

        <Row>
          <Column sm={12} md={6} lg={6}>
            <TextInput
              id="conectaNegociosFinancing"
              labelText="5.4.1. Financiamento do Conecta Negócios"
              maxLength={12}
              disabled={disabled}
              helperText="(não mais que 1.000.000,00MZN)"
              {...register("conectaNegociosFinancing")}
              onBlur={handleFinancingBlur}
            />
            <p className="field-error">
              {errors.conectaNegociosFinancing?.message}
            </p>
          </Column>
          <Column sm={12} md={6} lg={6}>
            <div
              className="bx--form__helper-text"
              style={{ marginTop: "2rem" }}
            >
              ,00MZN
            </div>
          </Column>
        </Row>
      </fieldset>
    </FormGroup>
  );
};

FinancialPlanForm.defaultProps = {
  disabled: false,
};
export default FinancialPlanForm;
